import React, { useState } from 'react';
import {
  useListTechLanesQuery,
  useCreateTechLaneMutation,
  useSetTechLaneActiveMutation,
  useDeleteTechLaneMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const sectionCard = (title, icon, children) => (
  <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
    <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
      <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
    </div>
    <div style={{ padding: '1.25rem' }}>{children}</div>
  </div>
);

export default function TechConfig() {
  const { data: lanes = [], isLoading } = useListTechLanesQuery({ include_inactive: true });
  const [createLane, { isLoading: isCreating }] = useCreateTechLaneMutation();
  const [setLaneActive] = useSetTechLaneActiveMutation();
  const [deleteLane] = useDeleteTechLaneMutation();

  const [newLane, setNewLane] = useState({ name: '', sort_order: 0 });
  const [actionError, setActionError] = useState(null);

  const inputStyle = {
    padding: '8px 12px', border: `1px solid ${brand.softGray}`,
    borderRadius: '6px', fontSize: '0.875rem', color: brand.deepBlue, outline: 'none', fontFamily: 'inherit',
  };
  const btnPrimary = {
    background: brand.deepBlue, color: 'white', border: 'none', borderRadius: '6px',
    padding: '8px 16px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
  };

  const addLane = async () => {
    setActionError(null);
    if (!newLane.name.trim()) return;
    try {
      await createLane({
        name: newLane.name.trim(),
        sort_order: Number(newLane.sort_order) || 0,
      }).unwrap();
      setNewLane({ name: '', sort_order: 0 });
    } catch (err) {
      setActionError(err?.data?.detail || 'Could not add lane.');
    }
  };

  const toggleActive = async (lane) => {
    setActionError(null);
    try {
      await setLaneActive({ id: lane.id, is_active: !lane.is_active }).unwrap();
    } catch (err) {
      setActionError(err?.data?.detail || 'Could not update lane.');
    }
  };

  const removeLane = async (lane) => {
    setActionError(null);
    const ok = window.confirm(`Delete lane "${lane.name}"? Projects using it will become unassigned.`);
    if (!ok) return;
    try {
      await deleteLane({ id: lane.id }).unwrap();
    } catch (err) {
      setActionError(err?.data?.detail || 'Could not delete lane.');
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Tech Config</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
          Manage dropdown options used in the Projects board.
        </p>
      </div>

      {actionError && (
        <div style={{ background: '#fdecea', color: '#b00020', borderRadius: '8px', padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {actionError}
        </div>
      )}

      {sectionCard('Project Lanes', 'signpost-split',
        <>
          <div style={{ background: brand.softGray, borderRadius: '8px', padding: '1rem', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <input
              style={{ ...inputStyle, flex: '1 1 200px' }}
              placeholder="New lane name"
              value={newLane.name}
              onChange={e => setNewLane(l => ({ ...l, name: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') addLane(); }}
            />
            <input
              style={{ ...inputStyle, width: '120px' }}
              type="number"
              placeholder="Sort"
              value={newLane.sort_order}
              onChange={e => setNewLane(l => ({ ...l, sort_order: e.target.value }))}
            />
            <button style={btnPrimary} onClick={addLane} disabled={isCreating || !newLane.name.trim()}>
              {isCreating ? 'Adding…' : 'Add Lane'}
            </button>
          </div>

          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <div className="spinner-border" style={{ color: brand.tealBlue }} />
            </div>
          ) : lanes.length === 0 ? (
            <div style={{ textAlign: 'center', color: brand.charcoal, padding: '2rem', fontSize: '0.9rem' }}>
              No lanes yet. Add one above.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${brand.softGray}` }}>
                  {['Name', 'Sort', 'Active', ''].map(h => (
                    <th key={h} style={{ padding: '0.6rem 0.75rem', color: brand.charcoal, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lanes.map(lane => (
                  <tr key={lane.id} style={{ borderBottom: `1px solid ${brand.softGray}`, opacity: lane.is_active ? 1 : 0.55 }}>
                    <td style={{ padding: '0.75rem', fontWeight: 600, color: brand.deepBlue }}>{lane.name}</td>
                    <td style={{ padding: '0.75rem', color: brand.charcoal, fontSize: '0.875rem' }}>{lane.sort_order}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: brand.charcoal }}>
                        <input
                          type="checkbox"
                          checked={!!lane.is_active}
                          onChange={() => toggleActive(lane)}
                        />
                        {lane.is_active ? 'Active' : 'Inactive'}
                      </label>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      <button
                        title="Delete lane"
                        onClick={() => removeLane(lane)}
                        style={{ background: 'transparent', border: 'none', color: '#b00020', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
